import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    statusUrl: String,
    running: Boolean,
  };

  static targets = [
    "status",
    "statusText",
    "pulse",
    "stage",
    "progress",
    "progressBar",
    "progressBarWrap",
    "detail",
    "finishedAt",
    "errorCount",
    "errorMessage",
    "preview",
    "fetchedCount",
    "importedCount",
    "newCount",
    "updatedCount",
    "zeroedCount",
    "actions",
    "childRunsBody",
    "childSearch",
    "childCount",
  ];

  connect() {
    if (!this.runningValue) return;

    this.poll();
    this.timer = window.setInterval(() => this.poll(), 3000);
  }

  disconnect() {
    if (this.timer) {
      window.clearInterval(this.timer);
    }
  }

  async poll() {
    const response = await fetch(this.statusUrlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return;

    const payload = await response.json();
    const run = payload.data;

    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = run.status;
    } else {
      this.statusTarget.textContent = run.status;
    }

    if (this.hasPulseTarget) {
      this.pulseTarget.style.display = run.running ? "" : "none";
    }

    this.stageTarget.textContent = run.stage_label || this.translateStage(run.progress_stage);

    const hasPercent = run.progress_percent != null;
    const current = run.progress_current || 0;

    if (hasPercent) {
      this.progressTarget.textContent = `${run.progress_percent}% (${current.toLocaleString("pl-PL")}/${(run.progress_total || 0).toLocaleString("pl-PL")})`;
    } else if (current > 0) {
      this.progressTarget.textContent = `${current.toLocaleString("pl-PL")} wierszy`;
    } else {
      this.progressTarget.textContent = "-";
    }

    if (this.hasProgressBarTarget) {
      if (hasPercent) {
        this.progressBarTarget.className = "h-full rounded-full bg-indigo-500 transition-all duration-500";
        this.progressBarTarget.style.width = `${run.progress_percent}%`;
      } else {
        this.progressBarTarget.className = "h-full w-1/3 rounded-full bg-indigo-400/60 animate-indeterminate";
        this.progressBarTarget.style.width = "";
      }
    }

    if (this.hasDetailTarget) {
      const parts = [run.seller, run.current_seller].filter(Boolean);
      this.detailTarget.textContent = parts.join(" · ");
    }
    this.finishedAtTarget.textContent = run.finished_at || "-";
    this.errorCountTarget.textContent = String(run.error_count || 0);
    this.errorMessageTarget.textContent = run.error_message || "-";
    this.previewTarget.textContent = [...(run.errors_preview || []), ...(run.warnings || [])].join("\n") || "Brak podglądu błędów.";
    this.fetchedCountTarget.textContent = String(run.fetched_count || 0);
    this.importedCountTarget.textContent = String(run.imported_count || 0);
    this.newCountTarget.textContent = String(run.new_count || 0);
    this.updatedCountTarget.textContent = String(run.updated_count || 0);
    this.zeroedCountTarget.textContent = String(run.zeroed_count || 0);

    if (this.hasChildRunsBodyTarget && run.child_runs) {
      this.updateChildRuns(run.child_runs);
    }

    if (!run.running && this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
      window.location.reload();
    }
  }

  translateStage(stage) {
    const map = {
      queued: "W kolejce",
      parsing: "Parsowanie CSV",
      fetching: "Pobieranie plików",
      importing: "Importowanie",
      saving: "Zapis do bazy",
      cleanup: "Czyszczenie ofert",
      syncing: "Synchronizacja",
      finalizing: "Finalizacja",
      cancelled: "Przerwany",
    };
    return map[stage] || stage || "-";
  }

  updateChildRuns(childRuns) {
    if (!childRuns.length) return;

    const badgeVariant = (status, errorCount) => {
      if (status === "running") return "warning";
      if (status === "cancelled") return "muted";
      if (errorCount > 0 || status === "failed") return "danger";
      return "success";
    };

    const esc = (s) => {
      const d = document.createElement("div");
      d.textContent = s;
      return d.innerHTML;
    };

    const rows = childRuns
      .map((cr) => {
        const v = badgeVariant(cr.status, cr.error_count);
        const badgeCls = {
          warning: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20",
          muted: "bg-slate-100 text-slate-600",
          danger: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20",
          success: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20",
        }[v];
        const badge = `inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${badgeCls}`;
        const td = "px-6 py-3 text-sm";
        return `<tr data-company="${esc(cr.company_name)}" data-branch="${esc(cr.branch_name)}">
          <td class="${td} font-medium">${esc(cr.company_name)}</td>
          <td class="${td} text-slate-500">${esc(cr.branch_name)}</td>
          <td class="${td}"><span class="${badge}">${esc(cr.status)}</span></td>
          <td class="${td} text-right text-slate-500">${cr.fetched_count}</td>
          <td class="${td} text-right text-slate-500">${cr.new_count}</td>
          <td class="${td} text-right text-slate-500">${cr.updated_count}</td>
          <td class="${td} text-right text-slate-500">${cr.zeroed_count}</td>
          <td class="${td} text-right text-slate-500">${cr.error_count}</td>
        </tr>`;
      })
      .join("");

    this.childRunsBodyTarget.innerHTML = rows;

    if (this.hasChildCountTarget) {
      this.childCountTarget.textContent = `(${childRuns.length})`;
    }

    this.filterChildRuns();
  }

  filterChildRuns() {
    if (!this.hasChildSearchTarget || !this.hasChildRunsBodyTarget) return;

    const query = this.childSearchTarget.value.trim().toLowerCase();
    const rows = this.childRunsBodyTarget.querySelectorAll("tr[data-company]");
    let visible = 0;

    rows.forEach((row) => {
      const company = (row.dataset.company || "").toLowerCase();
      const branch = (row.dataset.branch || "").toLowerCase();
      const match = !query || company.includes(query) || branch.includes(query);
      row.style.display = match ? "" : "none";
      if (match) visible++;
    });

    if (this.hasChildCountTarget) {
      const total = rows.length;
      this.childCountTarget.textContent = query ? `(${visible}/${total})` : `(${total})`;
    }
  }
}
